import { resolveProductSelectLabel } from "./gpsrLabelMappers";

/** Prefer API options when non-empty; otherwise backend-aligned fallbacks. */
export function pickGpsrSelectOptions(apiOptions, fallbackOptions) {
  return Array.isArray(apiOptions) && apiOptions.length > 0
    ? apiOptions
    : fallbackOptions;
}

export function buildGpsrCountryOptions(countries = []) {
  if (!Array.isArray(countries)) return [];
  return countries
    .map((country) => {
      const code = String(country?.code ?? "").trim();
      if (!code) return null;
      return {
        value: code,
        label: country.name_no_article || country.name || code,
      };
    })
    .filter(Boolean);
}

export const FALLBACK_PRODUCT_CATEGORY_OPTIONS = [
  { value: "clothing_textile", label: "Clothing or textile" },
  { value: "toy", label: "Toy" },
  { value: "electrical_electronic", label: "Electrical or electronic" },
  { value: "battery", label: "Battery product" },
  { value: "protective_equipment", label: "Personal protective equipment" },
  { value: "furniture", label: "Furniture" },
  { value: "jewellery", label: "Jewellery" },
  { value: "childcare", label: "Childcare product" },
  { value: "cosmetic", label: "Cosmetic" },
  { value: "food_supplement", label: "Food or supplement" },
  { value: "medical", label: "Medical product" },
  { value: "machinery", label: "Machinery" },
  { value: "household", label: "General household product" },
  { value: "other", label: "Other" },
];

export const FALLBACK_WEIGHT_UNIT_OPTIONS = [
  { value: "g", label: "g" },
  { value: "kg", label: "kg" },
];

const COMPACT_WEIGHT_UNIT_LABELS = {
  g: "g",
  kg: "kg",
};

export function normalizeGpsrWeightUnitOptions(options) {
  if (!Array.isArray(options) || options.length === 0) {
    return FALLBACK_WEIGHT_UNIT_OPTIONS;
  }
  return options.map((opt) => {
    if (opt == null || typeof opt !== "object") return opt;
    const value = String(opt.value ?? opt.id ?? opt.code ?? "").trim();
    const compactLabel = COMPACT_WEIGHT_UNIT_LABELS[value];
    if (!compactLabel) return opt;
    return { ...opt, value: value || opt.value, label: compactLabel };
  });
}

export const FALLBACK_REPRESENTATIVE_TYPE_OPTIONS = [
  { value: "lappa", label: "Lappa" },
  { value: "custom", label: "Custom" },
];

export const SUPPORTED_REPRESENTATIVE_TYPE_VALUES = new Set(["lappa", "custom"]);

export function normalizeGpsrRepresentativeMode(rawType) {
  const normalized = String(rawType ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return "";
  if (normalized === "custom") return "custom";
  if (normalized === "lappa") return "lappa";
  return "";
}

export function buildGpsrRepresentativeTypeOptions(apiOptions) {
  const source = pickGpsrSelectOptions(
    apiOptions,
    FALLBACK_REPRESENTATIVE_TYPE_OPTIONS
  );
  const normalized = (Array.isArray(source) ? source : [])
    .map((opt) => {
      if (opt == null || typeof opt !== "object") return null;
      const rawValue = String(opt.value ?? opt.id ?? opt.code ?? "").trim();
      const value = normalizeGpsrRepresentativeMode(rawValue) || rawValue;
      if (!value) return null;
      const label = String(opt.label ?? opt.name ?? value).trim() || value;
      return { value, label };
    })
    .filter(Boolean);

  const interactive = normalized.filter((option) =>
    SUPPORTED_REPRESENTATIVE_TYPE_VALUES.has(option.value)
  );
  return interactive.length > 0
    ? interactive
    : FALLBACK_REPRESENTATIVE_TYPE_OPTIONS;
}

export const FALLBACK_USE_RESTRICTION_OPTIONS = [
  { value: "no_warning_required", label: "No special warning required" },
  { value: "cold_drinks_only", label: "Cold drinks only" },
  { value: "max_liquid_temperature", label: "Maximum liquid temperature" },
  {
    value: "no_carbonated_drinks",
    label: "Not suitable for carbonated drinks",
  },
  { value: "not_microwave_safe", label: "Not microwave-safe" },
  { value: "not_dishwasher_safe", label: "Not dishwasher-safe" },
  { value: "wash_before_first_use", label: "Wash before first use" },
  { value: "keep_away_from_heat", label: "Keep away from heat or open flame" },
  { value: "other", label: "Other warning" },
];

export const FALLBACK_CARE_SYMBOL_OPTIONS = [
  { value: "machine_wash", label: "Machine wash" },
  { value: "hand_wash", label: "Hand wash" },
  { value: "do_not_wash", label: "Do not wash" },
  { value: "do_not_bleach", label: "Do not bleach" },
  { value: "tumble_dry", label: "Tumble dry" },
  { value: "do_not_tumble_dry", label: "Do not tumble dry" },
  { value: "dry_flat", label: "Dry flat" },
  { value: "line_dry", label: "Line dry" },
  { value: "iron_at_low_temperature", label: "Iron at low temperature" },
  { value: "iron_at_medium_temperature", label: "Iron at medium temperature" },
  { value: "iron_at_high_temperature", label: "Iron at high temperature" },
  { value: "do_not_iron", label: "Do not iron" },
  { value: "professional_dry_clean", label: "Professional dry clean" },
  { value: "do_not_dry_clean", label: "Do not dry clean" },
];

export const FALLBACK_MINIMUM_USER_AGE_OPTIONS = [
  { value: "no_minimum_age", label: "No minimum age" },
  { value: "0+", label: "0+" },
  { value: "3+", label: "3+" },
  { value: "6+", label: "6+" },
  { value: "8+", label: "8+" },
  { value: "12+", label: "12+" },
  { value: "14+", label: "14+" },
  { value: "16+", label: "16+" },
  { value: "18+", label: "18+" },
];

export const FALLBACK_FIBRE_COMPONENT_TYPE_OPTIONS = [
  { value: "main_fabric", label: "Main fabric" },
  { value: "lining", label: "Lining" },
  { value: "filling", label: "Filling" },
  { value: "trim", label: "Trim" },
  { value: "coating", label: "Coating" },
  { value: "other", label: "Other" },
];

export const FALLBACK_FIBRE_TYPE_OPTIONS = [
  { value: "cotton", label: "Cotton" },
  { value: "flax_linen", label: "Flax (Linen)" },
  { value: "hemp", label: "Hemp" },
  { value: "jute", label: "Jute" },
  { value: "bamboo", label: "Bamboo" },
  { value: "ramie", label: "Ramie" },
  { value: "sisal", label: "Sisal" },
  { value: "wool", label: "Wool" },
  { value: "silk", label: "Silk" },
  { value: "cashmere", label: "Cashmere" },
  { value: "mohair", label: "Mohair" },
  { value: "alpaca", label: "Alpaca" },
  { value: "angora", label: "Angora" },
  { value: "camel_hair", label: "Camel Hair" },
  { value: "polyester", label: "Polyester" },
  { value: "nylon_polyamide", label: "Nylon (Polyamide)" },
  { value: "acrylic", label: "Acrylic" },
  { value: "spandex_elastane", label: "Spandex (Elastane)" },
  { value: "viscose_rayon", label: "Viscose (Rayon)" },
  { value: "lyocell_tencel", label: "Lyocell (Tencel)" },
];

export function migrateLegacyGpsrProductType(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const match = FALLBACK_PRODUCT_CATEGORY_OPTIONS.find(
    (option) => option.label === raw || option.value === raw
  );
  return match ? match.value : raw;
}

export function resolveGpsrOptionLabel(options, value) {
  return resolveProductSelectLabel(options, value);
}
